/**
 * LyraDocumentManagement — Admin
 * Gestion de la base de connaissance de Lyra (documents PDF indexés).
 * Canal principal : upload PDF depuis l'interface admin.
 * Pattern identique à OrionDocumentManagement, couleur amber.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { FileText, Upload, Trash2, RefreshCw, Loader, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import {
  getLyraDocuments, uploadLyraPdf, deleteLyraDocument, reindexLyraDocument,
} from '../../api';

// ─── Formulaire upload ─────────────────────────────────────────────────────────

const UploadForm = ({ onSave, onCancel }) => {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: '' });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    if (!form.name) {
      setForm(prev => ({ ...prev, name: f.name.replace(/\.[^.]+$/, '') }));
    }
  };

  const handleSubmit = async () => {
    if (!file) { setError('Veuillez sélectionner un fichier'); return; }
    if (!form.name.trim()) { setError('Nom du document requis'); return; }

    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', form.name.trim());
      await uploadLyraPdf(fd);
      onSave();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400';

  return (
    <div className="border border-amber-200 rounded-xl bg-amber-50/50 p-5 space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-700">
        <Upload size={15} />
        Ajouter un document
      </div>

      <div
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${
          file ? 'border-amber-300 bg-amber-50' : 'border-gray-200 hover:border-amber-200 hover:bg-gray-50'
        }`}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={handleFileChange}
        />
        {file ? (
          <div className="space-y-1">
            <FileText size={20} className="mx-auto text-amber-500" />
            <p className="text-sm font-medium text-gray-800">{file.name}</p>
            <p className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} Mo</p>
          </div>
        ) : (
          <div className="space-y-1 text-gray-400">
            <Upload size={20} className="mx-auto opacity-50" />
            <p className="text-sm">Cliquez pour sélectionner un fichier</p>
            <p className="text-xs">PDF — max 10 Mo</p>
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Nom du document *</label>
        <input className={inputCls} value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="Ex: Protocole nettoyage HACCP 2024" />
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200">
          Annuler
        </button>
        <button onClick={handleSubmit} disabled={uploading}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-lg disabled:opacity-50">
          {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Upload en cours…' : 'Uploader et indexer'}
        </button>
      </div>
    </div>
  );
};

// ─── Ligne document ────────────────────────────────────────────────────────────

const DocRow = ({ doc, onDelete, onReindex }) => {
  const [expanded, setExpanded] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleReindex = async () => {
    setReindexing(true);
    try { await reindexLyraDocument(doc.id); onReindex(); } catch {}
    setReindexing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer "${doc.name}" ?`)) return;
    setDeleting(true);
    try { await deleteLyraDocument(doc.id); onDelete(); } catch {}
    setDeleting(false);
  };

  const chunkCount = doc._count?.chunks ?? 0;
  const isPdf = doc.mimeType === 'application/pdf';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
        <button onClick={() => setExpanded(e => !e)} className="text-gray-400 hover:text-gray-600">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-gray-400 flex-shrink-0" />
            <span className="font-medium text-gray-800 text-sm truncate">{doc.name}</span>
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-gray-400">
              {chunkCount} chunk{chunkCount !== 1 ? 's' : ''} indexé{chunkCount !== 1 ? 's' : ''}
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
              doc.indexed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              {doc.indexed ? '✓ Indexé' : '⏳ En attente'}
            </span>
            {doc.fileSize && (
              <span className="text-xs text-gray-400">{(doc.fileSize / 1024).toFixed(0)} Ko</span>
            )}
            <span className="text-xs text-gray-400">
              {new Date(doc.uploadedAt).toLocaleDateString('fr-FR')}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {doc.fileUrl && (
            <a href={doc.fileUrl} target="_blank" rel="noreferrer"
              className="p-1.5 text-gray-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition"
              title="Ouvrir le fichier">
              <ExternalLink size={14} />
            </a>
          )}
          {isPdf && (
            <button onClick={handleReindex} disabled={reindexing} title="Ré-indexer"
              className="p-1.5 text-gray-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition disabled:opacity-40">
              {reindexing ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </button>
          )}
          <button onClick={handleDelete} disabled={deleting} title="Supprimer"
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition disabled:opacity-40">
            {deleting ? <Loader size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        </div>
      </div>

      {expanded && doc.slug && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-2">
          <p className="text-xs text-gray-400 font-mono">{doc.slug}</p>
        </div>
      )}
    </div>
  );
};

// ─── Composant principal ───────────────────────────────────────────────────────

const LyraDocumentManagement = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLyraDocuments();
      setDocs(res.data || []);
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const totalChunks = docs.reduce((acc, d) => acc + (d._count?.chunks || 0), 0);
  const indexedCount = docs.filter(d => d.indexed).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText size={16} className="text-amber-500" />
            Base de connaissance — Lyra
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 rounded-lg px-4 py-3 text-sm text-amber-800 border border-amber-100">
            <strong>Documents PDF</strong> — Uploadez vos protocoles, fiches techniques HACCP, procédures sanitaires.
            Lyra les consulte pour répondre aux questions réglementaires (RAG).
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Documents', value: docs.length },
              { label: 'Chunks indexés', value: totalChunks },
              { label: 'Indexés', value: indexedCount },
            ].map(s => (
              <div key={s.label} className="bg-gray-50 rounded-lg px-3 py-2 text-center">
                <div className="text-lg font-bold text-amber-500">{s.value}</div>
                <div className="text-xs text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Bouton ajouter */}
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 border border-dashed border-amber-300 rounded-xl text-sm text-amber-600 hover:bg-amber-50 w-full justify-center transition">
              <Upload size={16} />
              Uploader un document PDF
            </button>
          )}

          {showForm && (
            <UploadForm
              onSave={() => { setShowForm(false); fetchDocs(); }}
              onCancel={() => setShowForm(false)}
            />
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader className="animate-spin text-amber-500" size={24} />
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <FileText size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucun document. Uploadez votre premier protocole pour commencer.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map(doc => (
                <DocRow
                  key={doc.id}
                  doc={doc}
                  onDelete={fetchDocs}
                  onReindex={fetchDocs}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info indexation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm text-gray-600">Fonctionnement de l'indexation</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-xs text-gray-600 space-y-2 leading-relaxed">
            <li><span className="font-medium text-gray-700">PDF</span> — Le texte est extrait puis découpé en chunks. Lyra consulte les chunks les plus pertinents à chaque question.</li>
            <li><span className="font-medium text-gray-700">Ré-indexer</span> — Si l'indexation a échoué (statut "En attente"), cliquez sur le bouton ré-indexer.</li>
            <li><span className="font-medium text-gray-700">Validation Drive</span> — Les documents envoyés via N8N (Google Drive) apparaissent dans l'onglet "Validation Drive" pour approbation avant indexation.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default LyraDocumentManagement;
